import type { ReactNode } from 'react';

import { Avatar } from '@/shared/ui';

import styles from './AccessShared.module.css';

interface TabsProps<T extends string> {
  tabs: readonly { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
  label: string;
}

/** The tab strip of every access surface (boards and databases look and behave the same). */
export function AccessTabs<T extends string>({ tabs, value, onChange, label }: TabsProps<T>) {
  return (
    <div className={styles.tabs} role="tablist" aria-label={label}>
      {tabs.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={value === item.id}
          className={`${styles.tab} ${value === item.id ? styles.tabOn : ''}`}
          onClick={() => {
            onChange(item.id);
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

interface RowProps {
  userId: string;
  name: string;
  /** Appended to the shown name only ("(you)"). */
  suffix?: string;
  /** Why this person has access (small muted line under the name). */
  reason?: ReactNode;
  /** Role picker / label and row actions. */
  children?: ReactNode;
}

/** One participant row: avatar, name with the reason for the access, then the controls. */
export function AccessPersonRow({ userId, name, suffix, reason, children }: RowProps) {
  return (
    <li className={styles.person} data-testid="participant-row">
      <Avatar id={userId} name={name} />
      <span className={styles.personText}>
        <span>
          {name}
          {suffix ? ` ${suffix}` : ''}
        </span>
        {reason ? <span className={styles.sub}>{reason}</span> : null}
      </span>
      {children}
    </li>
  );
}
